import { useEffect, type ComponentType } from 'react';
import { type LayoutChangeEvent, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { CoverImage } from '../components/design/CoverImage';
import Animated, {
  Extrapolation,
  FadeInDown,
  interpolate,
  interpolateColor,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  type SharedValue,
} from 'react-native-reanimated';
import { Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { dropShadow } from '@kiips/ui';
import { useThemeColors, Icon } from '@kiips/ui';
import { DESIGN_TRIPS } from '../lib/design-data';
import { unsplashUrl } from '../lib/unsplash';
import {
  CollaborationIllustration,
  MapsTimingIllustration,
  NotesIllustration,
  ReorderIllustration,
} from '../components/design/LandingFeatureIllustrations';
import { colors } from '../theme/colors';
import { useAuth } from '../contexts/AuthContext';
import { DEMO_TRIP_ID, ensureDemoTripForAnonymous } from '../lib/sample-itinerary';
import { ResponsivePage } from '../components/design/ResponsivePage';
import { useAppTopBarHeight } from '../components/design/AppTopBar';
import { HeroSection } from '../components/design/HeroSection';
import { LandingPricingSection } from '../components/design/LandingPricingSection';
import { LandingFooter } from '../components/design/LandingFooter';
import type { BillingPeriod, PlanDescriptor } from '../lib/pricing-plans';

const HERO_HEIGHT = 480;

// The landing hero is the LCP element on the marketing page. It's
// preloaded with fetchpriority=high in `app/+html.tsx`; keep that
// preload href byte-for-byte in sync with this URL or the preload is
// wasted (the browser won't reuse a mismatched request).
const LANDING_HERO = unsplashUrl('photo-1488646953014-85cb44e25828', { w: 1000 });

type IllustrationComponent = ComponentType<{ progress: SharedValue<number> }>;

interface Feature {
  icon: IconName;
  title: string;
  body: string;
  Illustration: IllustrationComponent;
  theme?: 'dark-fade' | undefined;
}

const FEATURES: Feature[] = [
  {
    icon: 'format-list-bulleted',
    title: 'Reorder days in a tap',
    body: 'Plans bend around your day, not the other way around.',
    Illustration: ReorderIllustration,
  },
  {
    icon: 'sticky-note-2',
    title: 'Notes that organise themselves',
    body: 'Tag a line, drop a checkbox, find it anywhere.',
    Illustration: NotesIllustration,
  },
  {
    icon: 'map',
    title: 'Maps that respect timing',
    body: "See what's open when you're actually there.",
    Illustration: MapsTimingIllustration,
    theme: 'dark-fade',
  },
  {
    icon: 'people',
    title: 'Plan together, in real time',
    body: 'Invite anyone — viewer or editor, your call.',
    Illustration: CollaborationIllustration,
  },
];

/**
 * Canonical / — landing page in the new design language.
 *
 * Hero photo with scroll-driven parallax, polaroid stack that fans out
 * as it enters view, four scroll-scrubbed feature cards, footer CTA.
 * For anonymous users we seed a demo trip so `/trips` always has
 * content; the "View demo trip" CTA jumps straight there.
 */
export default function HomeScreen() {
  const router = useRouter();
  const theme = useThemeColors();
  const { isAuthenticated } = useAuth();
  const { height: viewportHeight } = useWindowDimensions();
  const appBarHeight = useAppTopBarHeight();
  const featuredTrips = DESIGN_TRIPS.slice(0, 3);

  // Seed the demo trip once for anonymous users so /trips already has
  // content when they get there. SplashGate guarantees the persisted
  // store has rehydrated before this screen mounts, so the call is safe
  // to issue synchronously (the previous 150 ms timeout workaround is
  // gone — see ADR-0068).
  useEffect(() => {
    if (isAuthenticated) return;
    ensureDemoTripForAnonymous();
  }, [isAuthenticated]);

  const scrollY = useSharedValue(0);
  const onScroll = useAnimatedScrollHandler((e) => {
    scrollY.value = e.contentOffset.y;
  });

  const heroImageStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: scrollY.value * 0.4 },
      { scale: interpolate(scrollY.value, [-200, 0], [1.15, 1], Extrapolation.CLAMP) },
    ],
  }));

  const heroContentStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [0, HERO_HEIGHT * 0.7], [1, 0.3], Extrapolation.CLAMP),
    transform: [
      { translateY: interpolate(scrollY.value, [0, HERO_HEIGHT], [0, -40], Extrapolation.CLAMP) },
    ],
  }));

  const polaroidStageY = useSharedValue(0);
  const polaroidSpread = useDerivedValue(() => {
    if (polaroidStageY.value === 0) return 0;
    return interpolate(
      scrollY.value,
      [polaroidStageY.value - viewportHeight, polaroidStageY.value - viewportHeight * 0.4],
      [0, 1],
      Extrapolation.CLAMP,
    );
  });

  const goToTrips = () => router.push('/trips');
  const goToDemo = () => {
    ensureDemoTripForAnonymous();
    router.push(`/trips/${DEMO_TRIP_ID}`);
  };
  const goToPricing = () => router.push('/pricing');

  // Free routes straight to sign-up / trips; paid plans hand off to
  // /pricing, which owns the checkout + signup-resume flow (ADR-0062).
  // The teaser's per-card selector picks the billing period; pass it through
  // so /pricing resumes on the SKU the user chose here.
  const handlePlanSelect = (plan: PlanDescriptor, billing: BillingPeriod) => {
    if (plan.id === 'free') {
      router.push(isAuthenticated ? '/trips' : '/signup');
      return;
    }
    router.push(`/pricing?plan=${plan.id}&billing=${billing}`);
  };

  return (
    <ResponsivePage showTopBar={false}>
    <Animated.ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ paddingBottom: 60 }}
      onScroll={onScroll}
      scrollEventThrottle={16}
    >
      {/* No entrance animation on the hero. reanimated `entering` renders
          the element `visibility:hidden` until JS runs the animation, which
          on a throttled connection gates the LCP hero image for ~10s (the
          bundle hasn't executed yet). The hero must paint immediately. */}
      <Animated.View>
      <HeroSection
        marginHorizontal={12}
        borderRadius={32}
        minHeight={HERO_HEIGHT}
        backgroundColor="$textOnSurface"
        imageSlot={
          <>
            <Animated.View style={[StyleSheet.absoluteFill, heroImageStyle]}>
              <CoverImage
                uri={LANDING_HERO}
                alt="Aerial view of a coastal travel destination"
                fill
                // LCP element: high priority so the browser fetches it
                // eagerly; the web <img> paints without waiting on JS.
                priority="high"
              />
            </Animated.View>
            <YStack
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              backgroundColor={colors.overlayDark}
              pointerEvents="none"
            />
          </>
        }
        rightSlot={
          <Pressable
            onPress={() => router.push(isAuthenticated ? '/trips' : '/login')}
            accessibilityLabel={isAuthenticated ? 'My trips' : 'Sign in'}
            style={({ pressed }) => [
              {
                height: 40,
                paddingHorizontal: 14,
                borderRadius: 20,
                backgroundColor: colors.borderOnDark,
                alignItems: 'center',
                justifyContent: 'center',
              },
              pressed && { opacity: 0.8 },
            ]}
          >
            <Text fontSize={14} fontWeight="700" color="$textOnDark">
              {isAuthenticated ? 'My trips' : 'Sign in'}
            </Text>
          </Pressable>
        }
      >
        <Animated.View style={[{ padding: 28, paddingTop: appBarHeight + 8, paddingBottom: 32 }, heroContentStyle]}>
          <Text fontSize={12} color="$textOnDark" opacity={0.7} fontWeight="600" letterSpacing={0.5} marginBottom={16}>
            For travellers who care about the details
          </Text>
          <Text
            fontSize={36}
            fontWeight="800"
            color="$textOnDark"
            lineHeight={40}
            $lg={{ fontSize: 52, lineHeight: 58 }}
            $xl={{ fontSize: 64, lineHeight: 70 }}
          >
            Painless planning.{"\n"}Treasured trips.
          </Text>
          <Text fontSize={15} color="$textOnDark" opacity={0.85} marginTop={14} lineHeight={22}>
            Plan trips you&apos;ll actually love. Drag, drop, and rearrange your way to a trip that fits the way you actually want to spend your days.
          </Text>
          <XStack gap={10} marginTop={28}>
            <Pressable
              onPress={goToTrips}
              accessibilityLabel={isAuthenticated ? 'Go to my trips' : 'Get started'}
              style={({ pressed }) => [
                {
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme.surface,
                  borderRadius: 28,
                  paddingLeft: 20,
                  paddingRight: 6,
                  paddingVertical: 6,
                  gap: 12,
                },
                pressed && { transform: [{ scale: 0.98 }] },
              ]}
            >
              {/* Colours come from `theme.*` (JS), matching the Pressable's
                  `theme.surface` background and the arrow icon below. Mixing
                  in Tamagui `$` tokens here resolves the label/circle via CSS
                  while the background lags on JS hydration, flashing an
                  illegible white-on-white label + black circle on load. */}
              <Text fontSize={15} fontWeight="700" color={theme.textOnSurface}>
                {isAuthenticated ? 'Go to my trips' : 'Get started'}
              </Text>
              <YStack
                width={32}
                height={32}
                borderRadius={999}
                backgroundColor={theme.surfaceCanvas}
                alignItems="center"
                justifyContent="center"
              >
                <Icon name="arrow-forward" size={16} color={theme.textOnSurface} />
              </YStack>
            </Pressable>
            <Pressable
              onPress={goToDemo}
              accessibilityLabel="View demo trip"
              style={({ pressed }) => [
                {
                  paddingHorizontal: 20,
                  paddingVertical: 14,
                  borderRadius: 28,
                  borderWidth: 1,
                  borderColor: colors.textOnDarkSubdued,
                },
                pressed && { transform: [{ scale: 0.98 }] },
              ]}
            >
              <Text color="$textOnDark" fontSize={14} fontWeight="600">View demo trip</Text>
            </Pressable>
          </XStack>
          <Pressable
            onPress={goToPricing}
            accessibilityLabel="See pricing"
            style={({ pressed }) => ({
              marginTop: 16,
              alignSelf: 'flex-start',
              opacity: pressed ? 0.7 : 0.85,
            })}
          >
            <Text color="$textOnDark" fontSize={13} fontWeight="600" textDecorationLine="underline">
              See pricing →
            </Text>
          </Pressable>
        </Animated.View>
      </HeroSection>
      </Animated.View>

      <YStack
        paddingHorizontal={28}
        paddingTop={56}
        paddingBottom={48}
        onLayout={(e: LayoutChangeEvent) => {
          polaroidStageY.value = e.nativeEvent.layout.y;
        }}
      >
        <Text fontSize={11} color="$textOnSurface" fontWeight="700" letterSpacing={1.2}>SEE IT IN ACTION</Text>
        <Text
          fontSize={28}
          fontWeight="800"
          color="$textOnSurface"
          lineHeight={32}
          marginTop={8}
          $lg={{ fontSize: 40, lineHeight: 46 }}
          $xl={{ fontSize: 48, lineHeight: 54 }}
        >
          Discover destinations,{"\n"}one polaroid at a time.
        </Text>
        <YStack height={320} alignItems="center" justifyContent="center" marginTop={28}>
          {featuredTrips.map((trip, i) => (
            <ScrollSpreadPolaroid
              key={trip.id}
              trip={trip}
              index={i}
              total={featuredTrips.length}
              spread={polaroidSpread}
              onPress={goToDemo}
            />
          ))}
        </YStack>
      </YStack>

      {FEATURES.map((feature, i) => (
        <FeatureCard
          key={feature.title}
          feature={feature}
          index={i}
          scrollY={scrollY}
          viewportHeight={viewportHeight}
        />
      ))}

      <LandingPricingSection onSelectPlan={handlePlanSelect} onSeeAllPlans={goToPricing} />

      {/* Social-proof band intentionally removed — it previously housed a
          hardcoded "200K+ travellers" vanity stat. Reintroduce it here once we
          have a real aggregate: expose a cached `publicProcedure` (e.g.
          `stats.public`) and gate rendering behind a credibility floor, only
          showing a live number once the count clears a threshold. */}
      <YStack paddingHorizontal={20} paddingTop={56} paddingBottom={28}>
        <Pressable
          onPress={goToTrips}
          accessibilityLabel={isAuthenticated ? 'Open my trips' : 'Start your first trip'}
          style={({ pressed }) => [
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              backgroundColor: colors.accentCerulean,
              height: 64,
              borderRadius: 32,
              // Accent-tinted glow so the primary CTA reads as the page's focal point.
              ...dropShadow({ offsetY: 8, radius: 20, opacity: 0.4, color: colors.accentCerulean }),
              elevation: 8,
            },
            pressed && { transform: [{ scale: 0.99 }] },
          ]}
        >
          {/* `theme.*` (JS) for the label/icon to match the Pressable's JS
              `colors.accentCerulean` background — a `$textInverse` CSS token
              would flash mismatched on load while the background lags hydration. */}
          <Text color={theme.textInverse} fontSize={16} fontWeight="700">
            {isAuthenticated ? 'Open my trips' : 'Start your first trip'}
          </Text>
          <Icon name="arrow-forward" size={20} color={theme.textInverse} />
        </Pressable>
        <Text textAlign="center" fontSize={12} color="$textSubdued" marginTop={12}>
          {isAuthenticated ? 'Synced across your devices.' : 'Free for personal use · No credit card required'}
        </Text>
        {!isAuthenticated ? (
          <Pressable
            onPress={goToPricing}
            accessibilityLabel="See pricing"
            style={({ pressed }) => ({
              marginTop: 6,
              alignSelf: 'center',
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Text fontSize={12} color="$textSubdued" fontWeight="600" textDecorationLine="underline">
              See pricing →
            </Text>
          </Pressable>
        ) : null}
      </YStack>

      <LandingFooter isAuthenticated={isAuthenticated} />
    </Animated.ScrollView>
    </ResponsivePage>
  );
}

interface ScrollSpreadPolaroidProps {
  trip: { id: string; hero: string; destination: string };
  index: number;
  total: number;
  spread: SharedValue<number>;
  onPress: () => void;
}

function ScrollSpreadPolaroid({ trip, index, total, spread, onPress }: ScrollSpreadPolaroidProps) {
  const theme = useThemeColors();
  const offset = index - (total - 1) / 2;

  const animatedStyle = useAnimatedStyle(() => {
    const p = spread.value;
    return {
      transform: [
        { translateX: offset * (14 + p * 110) },
        { translateY: index * (8 - p * 6) },
        { rotate: `${offset * (6 + p * 14)}deg` },
      ],
    };
  });

  return (
    <Animated.View
      entering={FadeInDown.delay(index * 90).duration(360)}
      style={[
        {
          position: 'absolute',
          width: 200,
          height: 280,
          backgroundColor: theme.surface,
          borderRadius: 18,
          padding: 8,
          ...dropShadow({ offsetY: 6, radius: 16, opacity: 0.1, color: theme.shadowDefault }),
          elevation: 6,
          zIndex: total - index,
        },
        animatedStyle,
      ]}
    >
      <Pressable
        onPress={onPress}
        accessibilityLabel={`Open demo trip — ${trip.destination}`}
        style={{ flex: 1 }}
      >
        <YStack flex={1} borderRadius={12} overflow="hidden" backgroundColor="$borderMuted">
          <CoverImage
            uri={trip.hero}
            alt={`${trip.destination} sample itinerary`}
            loading="lazy"
          />
        </YStack>
        <Text fontSize={14} fontWeight="700" color="$textOnSurface" marginTop={10} textAlign="center">
          {trip.destination}
        </Text>
      </Pressable>
    </Animated.View>
  );
}

interface FeatureCardProps {
  feature: Feature;
  index: number;
  scrollY: SharedValue<number>;
  viewportHeight: number;
}

function FeatureCard({ feature, index, scrollY, viewportHeight }: FeatureCardProps) {
  const theme = useThemeColors();
  // `theme.raw` = concrete hex for the scroll-driven `interpolateColor`
  // worklets below — they interpolate RGB channels and can't parse the
  // `var(--…)` strings the top-level values resolve to on web.
  const themeRaw = theme.raw;
  const cardY = useSharedValue(0);

  const onLayout = (e: LayoutChangeEvent) => {
    cardY.value = e.nativeEvent.layout.y;
  };

  const progress = useDerivedValue(() => {
    if (cardY.value === 0) return 0;
    return interpolate(
      scrollY.value,
      [cardY.value - viewportHeight, cardY.value - viewportHeight * 0.4],
      [0, 1],
      Extrapolation.CLAMP,
    );
  });

  const isDarkFade = feature.theme === 'dark-fade';

  const cardStyle = useAnimatedStyle(() => {
    if (!isDarkFade) return {};
    return {
      backgroundColor: interpolateColor(progress.value, [0.3, 0.6], [themeRaw.surface, themeRaw.textOnSurface]),
    };
  });

  const titleStyle = useAnimatedStyle(() => {
    if (!isDarkFade) return {};
    return { color: interpolateColor(progress.value, [0.3, 0.6], [themeRaw.textOnSurface, themeRaw.textOnDark]) };
  });

  const bodyStyle = useAnimatedStyle(() => {
    if (!isDarkFade) return {};
    return { color: interpolateColor(progress.value, [0.3, 0.6], [themeRaw.textSubdued, '#B0B0B0']) };
  });

  const iconStyle = useAnimatedStyle(() => {
    if (!isDarkFade) return {};
    return { backgroundColor: interpolateColor(progress.value, [0.3, 0.6], [themeRaw.surfaceCanvas, '#1F2433']) };
  });

  const Illustration = feature.Illustration;
  const reverse = index % 2 === 1;

  return (
    <Animated.View
      style={[
        {
          flexDirection: reverse ? 'row-reverse' : 'row',
          alignItems: 'center',
          backgroundColor: theme.surface,
          borderRadius: 22,
          padding: 16,
          marginHorizontal: 20,
          marginBottom: 12,
          gap: 12,
        },
        cardStyle,
      ]}
      onLayout={onLayout}
    >
      <YStack flex={1} alignItems="center" justifyContent="center">
        <Illustration progress={progress} />
      </YStack>
      <YStack flex={1}>
        <Animated.View
          style={[
            {
              width: 36,
              height: 36,
              borderRadius: 11,
              backgroundColor: theme.surfaceCanvas,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 10,
            },
            iconStyle,
          ]}
        >
          <Icon
            name={feature.icon}
            size={18}
            color={isDarkFade ? theme.textOnDark : theme.textOnSurface}
          />
        </Animated.View>
        <Animated.Text style={[{ fontSize: 16, fontWeight: '700', color: theme.textOnSurface }, titleStyle]}>
          {feature.title}
        </Animated.Text>
        <Animated.Text style={[{ fontSize: 13, color: theme.textSubdued, marginTop: 4, lineHeight: 18 }, bodyStyle]}>
          {feature.body}
        </Animated.Text>
      </YStack>
    </Animated.View>
  );
}
